import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, Typography } from "antd";
import { AxiosError } from "axios";
import { bindCompanies } from "entities/companies";
import { getProfile } from "entities/viewer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "shared/lib/antd-global";
import ROUTES from "shared/routes";
import { Input } from "shared/ui/Input";
import { checkCode, refreshMethods, sendCode } from "../lib/auth-actions";
import { AuthError } from "../lib/auth-error";
import {
  AuthSession,
  clearAuthSession,
  FAILED_STATUSES,
  formatPhone,
  loadAuthSession,
} from "../lib/auth-session";
import { useAuthRestart } from "../lib/use-auth-restart";
import { useCountdown } from "../lib/use-countdown";
import { useStatusPolling } from "../lib/use-status-polling";
import { AuthChannel } from "../types";

export const CodeForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const ref = localStorage.getItem("bind-companies-ref");
  const refFromParams = searchParams.get("ref");
  const [session, setSession] = useState<AuthSession | null>(loadAuthSession);
  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [sendingChannel, setSendingChannel] = useState<AuthChannel>();
  const [isProfileEnabled, setIsProfileEnabled] = useState<boolean>(false);
  const cooldown = useCountdown(session?.nextSendAt);

  const lastDelivery = session?.deliveries[session.deliveries.length - 1];
  const channelName = (channel: AuthChannel) => t(`auth.channels.${channel}`);

  const { restart, handleAuthError, reloadSession, search } = useAuthRestart({
    expiresAt: session?.expiresAt,
    // После успешного входа сессия уже не нужна.
    watchExpiry: !isProfileEnabled,
  });

  const showError = (authError: AuthError, current: AuthSession | null) => {
    const result = handleAuthError(authError, current);
    if (result !== true) {
      setError(result);
    }
  };

  useEffect(() => {
    if (!session || !lastDelivery) {
      // Нечего подтверждать — возвращаем к выбору способа или вводу номера.
      if (session) {
        navigate(`${ROUTES.AUTH_METHODS}?${search}`, { replace: true });
      } else {
        restart();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useStatusPolling({
    enabled: !!session && !isProfileEnabled,
    onUpdate: setSession,
    // Ошибки опроса не показываем; закончившийся вход — на ввод номера.
    onError: (authError) =>
      handleAuthError(authError, loadAuthSession()) !== true,
  });

  const { mutate: bindCompaniesMutate } = useMutation({
    mutationFn: bindCompanies,
    onSuccess() {
      message.success("Update profile success");
      localStorage.removeItem("bind-companies-ref");
    },
    onError(error: AxiosError<{ message: string }>) {
      message.error(error.response?.data?.message || "Update profile error");
      localStorage.removeItem("bind-companies-ref");
    },
    retry: 2,
    retryDelay: 1000,
  });

  const { mutate: confirmCodeMutate, isPending: isConfirmCodeLoading } =
    useMutation({
      mutationFn: (value: string) => {
        const current = loadAuthSession();
        if (!current) {
          throw new AuthError({ code: "INVALID_SESSION" });
        }
        return checkCode(current, value);
      },
      onSuccess(accessToken) {
        localStorage.setItem("token", accessToken);
        clearAuthSession();

        if (ref || refFromParams) {
          bindCompaniesMutate(ref || refFromParams!);
        }

        setIsProfileEnabled(true);
      },
      onError(authError: AuthError) {
        showError(authError, loadAuthSession());
      },
    });

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
    enabled: isProfileEnabled,
    gcTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isProfileEnabled || !profile) return;

    if (
      profile.identity_verification_provider &&
      profile.need_identity_verification &&
      profile.identity_verification_blocked
    ) {
      navigate(ROUTES.IDENTITY_VERIFICATION);
    } else if (profile.need_register) {
      navigate(ROUTES.REGISTRATION);
    } else if (profile.need_sign_personal_data) {
      navigate(ROUTES.SIGN_PERSONAL_DATA);
    } else {
      const pathname = localStorage.getItem("pathname");
      if (pathname) {
        navigate(pathname);
      } else {
        navigate(ROUTES.MAIN);
      }
    }
  }, [profile, isProfileEnabled, navigate]);

  /** Новая отправка — только по действию пользователя. */
  const resend = async (channel: AuthChannel) => {
    let current = loadAuthSession();
    if (!current) {
      restart();
      return;
    }

    setError("");
    setSendingChannel(channel);
    try {
      // Telegram пропадает из channels после отправки — вернуть его можно
      // только повторным /methods.
      if (!current.channels.includes(channel)) {
        current = await refreshMethods(current);
        setSession(current);
        if (!current.channels.includes(channel)) {
          setError(t("auth.errors.channel_unavailable"));
          return;
        }
      }

      const next = await sendCode(current, channel);
      setSession(next);
    } catch (e) {
      const latest = reloadSession();
      if (!latest) {
        return;
      }
      setSession(latest);
      showError(e as AuthError, latest);
    } finally {
      setSendingChannel(undefined);
    }
  };

  const sentCode = () => {
    setError("");
    confirmCodeMutate(code.replace(/\D/g, ""));
  };

  if (!session || !lastDelivery) {
    return null;
  }

  const lastChannel = lastDelivery.channel;
  const isFailed = FAILED_STATUSES.includes(lastDelivery.delivery_status);
  const canSendSms = session.channels.includes("sms") && lastChannel !== "sms";
  const isSending = !!sendingChannel;

  return (
    <div>
      <Typography.Title level={3}>{t("auth.title")}</Typography.Title>

      <div>
        <p>
          {t("code.enter_code_from", { channel: channelName(lastChannel) })}
        </p>

        <Input
          mask="999999"
          name="additional-code"
          type="tel"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ textAlign: "center" }}
        />

        <p>
          {t(`code.status_${lastDelivery.delivery_status}`, {
            channel: channelName(lastChannel),
            phone: formatPhone(session.phone),
          })}
        </p>

        {isFailed && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            title={t("code.delivery_failed", {
              channel: channelName(lastChannel),
            })}
          />
        )}

        {!!error && (
          <Typography.Text
            style={{ marginBottom: 16, display: "block" }}
            type="danger"
          >
            {error}
          </Typography.Text>
        )}

        <Button
          block
          type="primary"
          disabled={code.replace(/\D/g, "").length < 6}
          onClick={sentCode}
          style={{ marginBottom: 16 }}
          loading={isConfirmCodeLoading || isProfileLoading}
        >
          {t("code.confirm")}
        </Button>

        {canSendSms && (
          <Button
            block
            onClick={() => resend("sms")}
            disabled={cooldown > 0 || isSending}
            loading={sendingChannel === "sms"}
            style={{ marginBottom: 16 }}
          >
            {cooldown > 0
              ? `${t("code.send_sms")} (${cooldown} ${t("code.seconds")})`
              : t("code.send_sms")}
          </Button>
        )}

        {!isFailed && (
          <Button
            block
            type="text"
            onClick={() => resend(lastChannel)}
            disabled={cooldown > 0 || isSending}
            loading={sendingChannel === lastChannel}
            style={{ marginBottom: 16 }}
          >
            {cooldown > 0
              ? `${t("code.resend_timer")} ${cooldown} ${t("code.seconds")}`
              : t("code.resend_code")}
          </Button>
        )}

        <Button
          block
          type="text"
          disabled={isSending}
          onClick={() => navigate(`${ROUTES.AUTH_METHODS}?${search}`)}
          style={{ marginBottom: 16 }}
        >
          {t("code.other_method")}
        </Button>

        <Button block type="text" onClick={() => restart()}>
          {t("code.change_number")}
        </Button>
      </div>
    </div>
  );
};
